// AdminNotification.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, Bell, User,
  ShoppingBag, AlertTriangle, Star,
  CreditCard, Settings, TrendingUp, Package, MessageSquare,
} from 'lucide-react';
import Swal from 'sweetalert2';
import axios from 'axios';
import AppContext from '../../../../context/AppContext';
import '../../../../assests/css/admin/AdminSidebar/AdminNotification.css';

// ── Icon map ─────────────────────────────────────────────────
const iconMap = {
  order:    ShoppingBag,
  warning:  AlertTriangle,
  payment:  CreditCard,
  system:   Settings,
  review:   Star,
  package:  Package,
  message:  MessageSquare,
  trending: TrendingUp,
  bell:     Bell,
  cart:     ShoppingBag,
  user:     User,
};

// ── Color map ─────────────────────────────────────────────────
const colorMap = {
  blue:    '#3b82f6',
  warning: '#fbbf24',
  success: '#4ade80',
  danger:  '#ef4444',
  info:    '#3b82f6',
  purple:  '#8b5cf6',
  order:   '#8b5cf6',
  payment: '#4ade80',
  review:  '#fbbf24',
  system:  '#3b82f6',
  package: '#ef4444',
  message: '#3b82f6',
  trending:'#8b5cf6',
  bell:    '#8b5cf6',
  cart:    '#8b5cf6',
  user:    '#3b82f6',
};

const getColor = (colorKey) => colorMap[colorKey] || '#8b5cf6';

const formatTime = (raw) => {
  if (!raw) return '';
  const date = new Date(raw);
  if (isNaN(date)) return raw;
  const diff = Math.floor((new Date() - date) / 1000);
  if (diff < 60)    return 'Just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return date.toLocaleDateString();
};

const mapNotification = (n) => ({
  id:          n.id,
  title:       n.title       || 'Notification',
  message:     n.description || '',
  fullDetails: n.detail      || n.description || '',
  icon:        iconMap[n.icon] || Bell,
  color:       n.icon        || 'bell',
  time:        formatTime(n.created_at),
  unread:      n.status === 'unread',   // ← fixed
});

// ── Per-item component ────────────────────────────────────────
const NotificationCard = ({ notification, index, onView, onDelete }) => {
  const Icon   = notification.icon;
  const view   = () => onView(notification);
  const remove = (e) => { e.stopPropagation(); onDelete(notification); };

  return (
    <div
      className={`NotiFication-Admin-Noti-item ${notification.unread ? 'unread' : ''}`}
      onClick={view}
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      <div className={`NotiFication-Admin-Noti-icon ${notification.color}`}>
        <Icon size={20} />
      </div>

      <div className="NotiFication-Admin-Noti-content">
        <div className="NotiFication-Admin-Noti-text-block">
          <h4 className="NotiFication-Admin-Noti-item-title">{notification.title}</h4>
          <p className="NotiFication-Admin-Noti-item-message">{notification.message}</p>
        </div>

        <div className="NotiFication-Admin-Noti-item-meta">
          <span className="NotiFication-Admin-Noti-item-time">{notification.time}</span>
          <div className="NotiFication-Admin-Noti-item-actions">
            {notification.unread && <div className="NotiFication-Admin-Noti-unread-badge" />}
            <button className="NotiFication-Admin-Noti-delete-btn" title="Delete notification" onClick={remove}>
              🗑
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════
const AdminNotification = () => {
  const { baseUrl } = useContext(AppContext);
  const navigate    = useNavigate();

  const [activeTab,     setActiveTab]     = useState('all');
  const [searchQuery,   setSearchQuery]   = useState('');
  const [visibleCount,  setVisibleCount]  = useState(5);
  const [notifications, setNotifications] = useState([]);
  const [loading,       setLoading]       = useState(true);

  const unreadCount = notifications.filter(n => n.unread).length;

  const filteredNotifications = notifications
    .filter(n => {
      if (activeTab === 'unread') return  n.unread;
      if (activeTab === 'read')   return !n.unread;
      return true;
    })
    .filter(n => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return n.title.toLowerCase().includes(q) || n.message.toLowerCase().includes(q);
    });

  const visibleNotifications = filteredNotifications.slice(0, visibleCount);

  // ── FETCH ─────────────────────────────────────────────────
  const fetchAll = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/notification/getnotifications`)
      .then((res) => {
        const raw = Array.isArray(res.data?.notifications) ? res.data.notifications : [];
        setNotifications(raw.map(mapNotification));
      })
      .catch((err) => {
        console.error('AdminNotification fetch error:', err);
        Swal.fire({
          icon: 'error', title: 'Failed to load',
          text: 'Could not load notifications.',
          confirmButtonColor: '#8b5cf6',
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, [baseUrl]);

  // ── VIEW (click) ──────────────────────────────────────────
  const viewNotification = (notification) => {
    Swal.fire({
      title:    notification.title,
      html:     `<div style="text-align:left;white-space:pre-line;color:#4b5563;line-height:1.7;font-size:0.95rem;">
                  ${notification.fullDetails || notification.message || 'No details available.'}
                 </div>`,
      icon:               'info',
      iconColor:          getColor(notification.color),
      confirmButtonColor: '#8b5cf6',
      confirmButtonText:  'Close',
      width:              '600px',
    });

    if (notification.unread) {
      setNotifications(prev =>
        prev.map(n => n.id === notification.id ? { ...n, unread: false } : n)
      );
      axios
        .post(`${baseUrl}/admin/notification/markasreadnotification`, { id: notification.id })
        .catch((err) => {
          console.error('Mark-read error:', err);
          // Revert on failure
          setNotifications(prev =>
            prev.map(n => n.id === notification.id ? { ...n, unread: true } : n)
          );
        });
    }
  };

  // ── DELETE ────────────────────────────────────────────────
  const removeNotification = (notification) => {
    Swal.fire({
      title:              'Delete Notification?',
      text:               'This notification will be permanently removed.',
      icon:               'warning',
      showCancelButton:   true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor:  '#6b7280',
      confirmButtonText:  'Yes, delete',
      cancelButtonText:   'Cancel',
    }).then((result) => {
      if (!result.isConfirmed) return;
      axios
        .post(`${baseUrl}/admin/notification/deletenotifications`, { id: notification.id })
        .then(() => {
          setNotifications(prev => prev.filter(n => n.id !== notification.id));
          Swal.fire({
            icon: 'success', title: 'Deleted!',
            text: 'Notification removed.',
            confirmButtonColor: '#8b5cf6',
            timer: 1500, showConfirmButton: false,
          });
        })
        .catch(err => {
          console.error('Delete error:', err);
          Swal.fire({
            icon: 'error', title: 'Error',
            text: 'Failed to delete notification.',
            confirmButtonColor: '#8b5cf6',
          });
        });
    });
  };

  // ── Tab / search setters ──────────────────────────────────
  const setTabAll    = () => { setActiveTab('all');    setVisibleCount(5); };
  const setTabUnread = () => { setActiveTab('unread'); setVisibleCount(5); };
  const setTabRead   = () => { setActiveTab('read');   setVisibleCount(5); };
  const updateSearch = (e) => { setSearchQuery(e.target.value); setVisibleCount(5); };
  const clearSearch  = () => { setSearchQuery(''); setVisibleCount(5); };
  const loadMore     = () => setVisibleCount(prev => prev + 5);
  const goBack       = () => navigate('/admin/dashboard');

  // ── RENDER ────────────────────────────────────────────────
  return (
    <div className="NotiFication-Admin-Noti-container">

      <div className="NotiFication-Admin-Noti-header">
        <div className="NotiFication-Admin-Noti-header-top">
          <button className="NotiFication-Admin-Noti-back-btn" onClick={goBack}>
            <ArrowLeft size={20} />
            <span>Back to Dashboard</span>
          </button>
        </div>

        <div className="NotiFication-Admin-Noti-header-main">
          <div className="NotiFication-Admin-Noti-title-section">
            <h1 className="NotiFication-Admin-Noti-title">
              Notifications
              {unreadCount > 0 && (
                <span className="NotiFication-Admin-Noti-header-badge">{unreadCount}</span>
              )}
            </h1>
            <p className="NotiFication-Admin-Noti-subtitle">
              Manage and view all your store activity updates.
            </p>
          </div>
        </div>
      </div>

      <div className="NotiFication-Admin-Noti-content">

        <div className="NotiFication-Admin-Noti-tabs-section">
          <div className="NotiFication-Admin-Noti-tabs">
            <button className={`NotiFication-Admin-Noti-tab ${activeTab === 'all'    ? 'active' : ''}`} onClick={setTabAll}>
              All <span className="NotiFication-Admin-Noti-tab-count">{notifications.length}</span>
            </button>
            <button className={`NotiFication-Admin-Noti-tab ${activeTab === 'unread' ? 'active' : ''}`} onClick={setTabUnread}>
              Unread
              {unreadCount > 0 && <span className="NotiFication-Admin-Noti-tab-count unread">{unreadCount}</span>}
            </button>
            <button className={`NotiFication-Admin-Noti-tab ${activeTab === 'read'   ? 'active' : ''}`} onClick={setTabRead}>
              Read <span className="NotiFication-Admin-Noti-tab-count">{notifications.filter(n => !n.unread).length}</span>
            </button>
          </div>

          <div className="NotiFication-Admin-Noti-actions">
            <div className="NotiFication-Admin-Noti-search">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchQuery}
                onChange={updateSearch}
              />
              {searchQuery && (
                <button className="NotiFication-Admin-Noti-search-clear" onClick={clearSearch}>×</button>
              )}
            </div>
          </div>
        </div>

        <div className="NotiFication-Admin-Noti-list">
          {loading ? (
            <div className="NotiFication-Admin-Noti-empty">
              <div className="NotiFication-Admin-Noti-empty-icon">⏳</div>
              <h3>Loading notifications...</h3>
              <p>Please wait</p>
            </div>
          ) : visibleNotifications.length === 0 ? (
            <div className="NotiFication-Admin-Noti-empty">
              <div className="NotiFication-Admin-Noti-empty-icon">🔔</div>
              <h3>No notifications found</h3>
              <p>{searchQuery ? 'No results match your search' : "You're all caught up!"}</p>
            </div>
          ) : (
            visibleNotifications.map((notification, index) => (
              <NotificationCard
                key={notification.id}
                notification={notification}
                index={index}
                onView={viewNotification}
                onDelete={removeNotification}
              />
            ))
          )}
        </div>

        {!loading && visibleNotifications.length < filteredNotifications.length && (
          <div className="NotiFication-Admin-Noti-load-more">
            <button className="NotiFication-Admin-Noti-load-more-btn" onClick={loadMore}>
              Load older notifications ({filteredNotifications.length - visibleNotifications.length} more)
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminNotification;